// src/pages/ServiceConnect.tsx: provider discovery marketplace (live data)
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import {
  C, CATEGORIES, catColor, hqLabel, signedAssetUrl,
  ServiceProvider, ServiceConnection,
} from '../lib/serviceConnect';

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

// Illustrative fallback shown only when no live providers exist yet.
const DEMO: ServiceProvider[] = [
  {
    id: 'demo-1', owner_id: null, name: 'Halstead & Reyes LLP', category: 'Legal',
    description: 'Full-service fund formation, regulatory counsel and ISDA negotiation for hedge funds, private credit and PE vehicles across Cayman, Delaware and UK structures.',
    headquarters_country: 'United States', headquarters_city: 'New York',
    regions_covered: ['US', 'UK', 'Global'], website: 'halsteadreyes.com', linkedin: null,
    founded_year: 2004, client_count: 120, logo_url: null, pitch_deck_url: null, demo_video_url: null,
    account_status: 'active', is_featured: true, created_at: '', updated_at: '',
  },
  {
    id: 'demo-2', owner_id: null, name: 'Meridian Fund Services', category: 'Fund Administration',
    description: 'Independent administrator handling NAV calculation, investor onboarding and AML/KYC for sub-$5B AUM hedge funds and private credit managers.',
    headquarters_country: 'Cayman Islands', headquarters_city: 'Grand Cayman',
    regions_covered: ['Global', 'Europe'], website: 'meridianfundservices.com', linkedin: null,
    founded_year: 2011, client_count: 84, logo_url: null, pitch_deck_url: null, demo_video_url: null,
    account_status: 'active', is_featured: true, created_at: '', updated_at: '',
  },
  {
    id: 'demo-3', owner_id: null, name: 'Enfusion', category: 'OMS/PMS',
    description: 'Cloud-native order and portfolio management with integrated accounting, real-time P&L, risk and compliance for multi-asset strategies in a single system.',
    headquarters_country: 'United States', headquarters_city: 'Chicago',
    regions_covered: ['North America', 'Europe', 'Asia Pacific'], website: 'enfusion.com', linkedin: null,
    founded_year: 1997, client_count: 850, logo_url: null, pitch_deck_url: null, demo_video_url: null,
    account_status: 'active', is_featured: false, created_at: '', updated_at: '',
  },
  {
    id: 'demo-4', owner_id: null, name: 'Whitmore Rhodes', category: 'Accounting',
    description: 'Fund audit, tax structuring and K-1 preparation for private funds, family offices and SPVs. PCAOB-registered with Big 4 alumni leadership.',
    headquarters_country: 'United States', headquarters_city: 'Boston',
    regions_covered: ['US'], website: 'whitmorerhodes.com', linkedin: null,
    founded_year: 2009, client_count: 60, logo_url: null, pitch_deck_url: null, demo_video_url: null,
    account_status: 'active', is_featured: false, created_at: '', updated_at: '',
  },
];

export default function ServiceConnect() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, firm } = useProfile();

  // Allocators (and candidates) have no ServiceConnect access, redirect them out.
  useEffect(() => {
    if (profile && profile.role === 'allocator') {
      navigate('/dashboard');
    }
  }, [profile, navigate]);

  // Providers do not browse the marketplace they are listed in, send them to
  // their own dashboard instead.
  useEffect(() => {
    if (profile && profile.role === 'service_provider') {
      navigate('/service-provider-dashboard');
    }
  }, [profile, navigate]);

  const isProvider = profile?.role === 'service_provider';

  const [providers, setProviders]   = useState<ServiceProvider[]>([]);
  const [isDemo, setIsDemo]         = useState(false);
  const [logos, setLogos]           = useState<Record<string, string>>({});
  const [productCounts, setCounts]  = useState<Record<string, number>>({});
  const [connections, setConns]     = useState<Record<string, ServiceConnection>>({});
  const [loading, setLoading]       = useState(true);
  const [connecting, setConnecting] = useState<string | null>(null);

  const [activeCat, setActiveCat] = useState('All');
  const [search, setSearch]       = useState('');

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [user, firm]);

  async function fetchAll() {
    setLoading(true);
    const { data } = await supabase
      .from('service_providers')
      .select('*')
      .eq('account_status', 'active')
      .order('is_featured', { ascending: false })
      .order('created_at', { ascending: false });

    const live = (data as ServiceProvider[] | null) || [];
    const list = live.length ? live : DEMO;
    setIsDemo(live.length === 0);
    setProviders(list);

    if (live.length) {
      const ids = live.map(p => p.id);
      const { data: prods } = await supabase
        .from('service_provider_products').select('provider_id').in('provider_id', ids);
      const counts: Record<string, number> = {};
      (prods || []).forEach((r: { provider_id: string }) => {
        counts[r.provider_id] = (counts[r.provider_id] || 0) + 1;
      });
      setCounts(counts);

      // Signed logo URLs
      const logoMap: Record<string, string> = {};
      await Promise.all(live.filter(p => p.logo_url).map(async p => {
        const url = await signedAssetUrl(p.logo_url);
        if (url) logoMap[p.id] = url;
      }));
      setLogos(logoMap);

      // Existing connections for this user
      if (user) {
        const { data: conns } = await supabase
          .from('service_connections').select('*').eq('requested_by', user.id);
        const cmap: Record<string, ServiceConnection> = {};
        (conns as ServiceConnection[] | null || []).forEach(c => { if (c.provider_id) cmap[c.provider_id] = c; });
        setConns(cmap);
      }
    }
    setLoading(false);
  }

  const handleConnect = async (p: ServiceProvider) => {
    if (isDemo || !user) return;
    if (!firm) { alert('You need a firm profile before you can connect with providers.'); return; }
    if (connections[p.id]) return;
    setConnecting(p.id);
    const { data, error } = await supabase.from('service_connections').insert({
      requester_firm_id: firm.id, provider_id: p.id, requested_by: user.id, status: 'pending',
    }).select().single();
    if (!error && data) {
      setConns(prev => ({ ...prev, [p.id]: data as ServiceConnection }));
      logActivity(user.id, firm.id, 'service_connection_made', 'service_provider', p.id, { provider_name: p.name, category: p.category });
    }
    else if (error) alert(error.message);
    setConnecting(null);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return providers.filter(p => {
      if (activeCat !== 'All' && p.category !== activeCat) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        hqLabel(p).toLowerCase().includes(q) ||
        (p.regions_covered || []).some(r => r.toLowerCase().includes(q)) ||
        p.category.toLowerCase().includes(q)
      );
    });
  }, [providers, activeCat, search]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: providers.length };
    providers.forEach(p => { counts[p.category] = (counts[p.category] || 0) + 1; });
    return counts;
  }, [providers]);

  const regionsCount = useMemo(() => {
    const set = new Set<string>();
    providers.forEach(p => (p.regions_covered || []).forEach(r => set.add(r)));
    return set.size;
  }, [providers]);

  const tabs = ['All', ...CATEGORIES];

  // Hold the listing back while the redirect above runs, so a provider never
  // sees the other providers even for a frame.
  if (loading || isProvider) return (
    <div style={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading providers…</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>ServiceConnect</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Discover and connect with vetted legal, fund administration, technology, data, insurance and advisory partners</p>
      </div>

      {isDemo && (
        <div style={{ background: C.navy, borderRadius: '4px', padding: '14px 18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ background: C.gold, color: C.navy, fontSize: '9px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: '2px', flexShrink: 0 }}>Preview</span>
          <span style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.5 }}>
            No providers have joined ServiceConnect yet. The firms below are illustrative, live listings and connections appear here as providers onboard.
          </span>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Providers', value: providers.length },
          { label: 'Categories', value: Object.keys(categoryCounts).filter(k => k !== 'All' && categoryCounts[k]).length },
          { label: 'Regions Covered', value: regionsCount || '-' },
        ].map(stat => (
          <div key={stat.label} style={{ ...card, padding: '14px 16px' }}>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.navy, marginBottom: '3px' }}>{stat.value}</div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs + search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {tabs.map(cat => (
            <button key={cat} onClick={() => setActiveCat(cat)} style={{
              padding: '6px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
              background: activeCat === cat ? C.navy : C.white,
              color: activeCat === cat ? C.white : C.textSec,
              border: activeCat === cat ? 'none' : `1px solid ${C.border}`,
              fontFamily: 'system-ui', fontWeight: 500,
            }}>
              {cat}
              <span style={{ marginLeft: '5px', opacity: 0.6, fontSize: '10px' }}>{categoryCounts[cat] || 0}</span>
            </button>
          ))}
        </div>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, category, region…"
          style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', color: C.text, fontSize: '13px', padding: '7px 12px', outline: 'none', fontFamily: 'system-ui', width: '280px', boxSizing: 'border-box' }}
          onFocus={e => (e.target.style.borderColor = C.navy)}
          onBlur={e => (e.target.style.borderColor = C.border)}
        />
      </div>

      <div style={{ fontSize: '11px', color: C.muted, marginBottom: '14px', fontWeight: 600, letterSpacing: '0.3px' }}>
        {filtered.length} provider{filtered.length !== 1 ? 's' : ''}{activeCat !== 'All' ? ` in ${activeCat}` : ''}{search ? ` matching "${search}"` : ''}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No providers found</div>
          <div style={{ fontSize: '13px', color: C.muted }}>Try adjusting your search or category filter</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          {filtered.map(p => {
            const conn = connections[p.id];
            const color = catColor(p.category);
            return (
              <div key={p.id} style={{
                ...card, padding: '18px 20px', display: 'flex', flexDirection: 'column',
                borderTop: p.is_featured ? `3px solid ${C.gold}` : `1px solid ${C.border}`,
              }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '11px', flex: 1, minWidth: 0 }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '4px', flexShrink: 0,
                      background: logos[p.id] ? C.white : C.navy, border: `1px solid ${C.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                      fontFamily: 'Georgia, serif', fontSize: '16px', color: C.gold, fontWeight: 700,
                    }}>
                      {logos[p.id] ? <img src={logos[p.id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : p.name.charAt(0)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '2px' }}>
                        <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.navy, fontWeight: 400 }}>{p.name}</div>
                        {p.is_featured && (
                          <span style={{ background: `${C.gold}22`, color: C.amber, border: `1px solid ${C.gold}44`, fontSize: '8px', fontWeight: 700, padding: '1px 5px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Featured</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: C.muted }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {hqLabel(p)}
                      </div>
                    </div>
                  </div>
                  <span style={{ background: `${color}14`, color, border: `1px solid ${color}33`, padding: '2px 8px', borderRadius: '2px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>{p.category}</span>
                </div>

                <p style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.6, margin: '0 0 12px', flex: 1 }}>{p.description}</p>

                {/* Regions */}
                {(p.regions_covered || []).length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {(p.regions_covered || []).map(r => (
                      <span key={r} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '2px' }}>{r}</span>
                    ))}
                  </div>
                )}

                {/* Meta */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '14px', fontSize: '11px', color: C.muted }}>
                  {p.client_count != null && <span><strong style={{ color: C.textSec }}>{p.client_count}</strong> clients</span>}
                  <span><strong style={{ color: C.textSec }}>{productCounts[p.id] || 0}</strong> product{(productCounts[p.id] || 0) !== 1 ? 's' : ''}</span>
                  {p.founded_year && <span>Est. <strong style={{ color: C.textSec }}>{p.founded_year}</strong></span>}
                </div>

                {/* Footer actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => navigate(`/service-connect/provider/${p.id}`)}
                    disabled={isDemo}
                    style={{
                      flex: 1, background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
                      borderRadius: '3px', padding: '7px 14px', fontSize: '11.5px', fontWeight: 600,
                      cursor: isDemo ? 'not-allowed' : 'pointer', fontFamily: 'system-ui', opacity: isDemo ? 0.6 : 1,
                    }}
                  >View Profile</button>
                  <button
                    onClick={() => handleConnect(p)}
                    disabled={isDemo || connecting === p.id || !!conn}
                    style={{
                      flex: 1,
                      background: conn?.status === 'approved' ? C.success : conn ? C.surface : C.navy,
                      color: conn?.status === 'approved' ? C.white : conn ? C.amber : C.white,
                      border: conn && conn.status !== 'approved' ? `1px solid ${C.amber}44` : 'none',
                      borderRadius: '3px', padding: '7px 14px', fontSize: '11.5px', fontWeight: 600,
                      cursor: isDemo || conn ? 'default' : 'pointer', fontFamily: 'system-ui',
                      opacity: isDemo ? 0.6 : 1,
                    }}
                  >
                    {connecting === p.id ? '…' : conn?.status === 'approved' ? 'Connected' : conn ? 'Pending' : 'Connect'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
